import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import api from '../utils/api';
import { useNotification as useToast } from '../components/Notification';

const NotificationInboxContext = createContext(null);

export function useNotificationInbox() {
  const ctx = useContext(NotificationInboxContext);
  if (!ctx) {
    throw new Error('useNotificationInbox must be used within NotificationInboxProvider');
  }
  return ctx;
}

export function NotificationInboxProvider({ children }) {
  const { user } = useAuth();
  const { showNotification } = useToast();
  const [unreadCount, setUnreadCount] = useState(0);
  const [recentItems, setRecentItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const prevUnreadRef = useRef(0);

  const fetchUnreadCount = useCallback(async () => {
    if (!user?._id) {
      setUnreadCount(0);
      return 0;
    }
    try {
      const { data } = await api.get('/notifications/unread-count');
      const count = data?.count ?? 0;
      setUnreadCount(count);
      return count;
    } catch {
      return 0;
    }
  }, [user?._id]);

  const fetchRecent = useCallback(async () => {
    if (!user?._id) {
      setRecentItems([]);
      return [];
    }
    setLoading(true);
    try {
      const { data } = await api.get('/notifications', { params: { limit: 8, page: 1 } });
      const items = data?.items || [];
      setRecentItems(items);
      if (typeof data?.unreadCount === 'number') setUnreadCount(data.unreadCount);
      return items;
    } catch {
      setRecentItems([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, [user?._id]);

  const refresh = useCallback(async () => {
    await Promise.all([fetchUnreadCount(), fetchRecent()]);
  }, [fetchUnreadCount, fetchRecent]);

  const markRead = useCallback(
    async (id) => {
      try {
        await api.patch(`/notifications/${id}/read`);
        setRecentItems((prev) => prev.map((n) => (n._id === id ? { ...n, read: true } : n)));
        setUnreadCount((c) => Math.max(0, c - 1));
      } catch {
        /* ignore */
      }
    },
    []
  );

  const markAllRead = useCallback(async () => {
    try {
      await api.patch('/notifications/read-all');
      setRecentItems((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch {
      /* ignore */
    }
  }, []);

  const deleteNotification = useCallback(async (id) => {
    try {
      await api.delete(`/notifications/${id}`);
      setRecentItems((prev) => {
        const removed = prev.find((n) => n._id === id);
        if (removed && !removed.read) setUnreadCount((c) => Math.max(0, c - 1));
        return prev.filter((n) => n._id !== id);
      });
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (!user?._id) {
      setUnreadCount(0);
      setRecentItems([]);
      prevUnreadRef.current = 0;
      return undefined;
    }
    let cancelled = false;
    let timer = null;
    const poll = async () => {
      if (cancelled) return;
      if (document.visibilityState === 'hidden') {
        timer = window.setTimeout(poll, 120000);
        return;
      }
      const prev = prevUnreadRef.current;
      const count = await fetchUnreadCount();
      if (cancelled) return;
      if (count > prev && prev >= 0) {
        try {
          const { data } = await api.get('/notifications', { params: { limit: 1, page: 1, unread: 'true' } });
          const latest = data?.items?.[0];
          if (latest?.title) {
            showNotification(latest.title, 'info', 5000);
          }
        } catch {
          /* ignore */
        }
        fetchRecent();
      }
      prevUnreadRef.current = count;
      timer = window.setTimeout(poll, 120000);
    };
    prevUnreadRef.current = 0;
    fetchUnreadCount().then((c) => {
      prevUnreadRef.current = c;
      timer = window.setTimeout(poll, 120000);
    });
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [user?._id, fetchUnreadCount, fetchRecent, showNotification]);

  const value = {
    unreadCount,
    recentItems,
    loading,
    refresh,
    fetchRecent,
    fetchUnreadCount,
    markRead,
    markAllRead,
    deleteNotification,
  };

  return (
    <NotificationInboxContext.Provider value={value}>{children}</NotificationInboxContext.Provider>
  );
}
